import React from 'react';
import { HeartOutlined } from '@ant-design/icons';
import './TodayIssueCard.css';

const stripPpMld = (rating) => String(rating || '')
  .replace(/PP\s*-\s*MLD/gi, '')
  .replace(/PPMLD/gi, '')
  .replace(/\(\s*CE\s*\)/gi, '')
  .trim();

const normalizeRating = (rating) => stripPpMld(rating).toUpperCase().replace(/\s+/g, '');

const getShortAgencyName = (fullName) => {
  if (!fullName) return fullName;
  const upperName = fullName.toUpperCase();
  if (upperName.includes('CRISIL')) return 'CRISIL';
  if (upperName.includes('ICRA')) return 'ICRA';
  if (upperName.includes('CARE') || upperName.includes('CREDIT ANALYSIS')) return 'CARE';
  if (upperName.includes('BRICKWORK')) return 'BRICKWORK';
  if (upperName.includes('INDIA RATING') || upperName.includes('INDIA RATINGS')) return 'INDIA';
  if (upperName.includes('ACUITE')) return 'ACUITE';
  if (upperName.includes('INFOMERICS')) return 'INFOMERICS';
  return fullName;
};

const pickPrimaryRating = (creditRatings) => {
  if (!creditRatings || !Array.isArray(creditRatings) || creditRatings.length === 0) return null;
  const sorted = [...creditRatings].filter(Boolean);
  const aaa = sorted.find((r) => normalizeRating(r?.currentRating).includes('AAA'));
  if (aaa) return aaa;
  return sorted[0];
};

const formatDateIndian = (dateString) => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateString;
  }
};

const formatIssuePriceInr = (value) => {
  if (value == null || value === '') return '—';
  const numValue = parseFloat(String(value).replace(/,/g, '').replace('₹', '').trim());
  if (!Number.isFinite(numValue)) return '—';
  if (numValue >= 10000000) {
    const crores = numValue / 10000000;
    return `₹${crores % 1 === 0 ? crores.toFixed(0) : crores.toFixed(1)} Cr`;
  }
  if (numValue >= 100000) {
    const lakhs = numValue / 100000;
    return `₹${lakhs % 1 === 0 ? lakhs.toFixed(0) : lakhs.toFixed(1)} Lac`;
  }
  return `₹${numValue.toLocaleString('en-IN')}`;
};

const resolvePaymentFrequencyLabel = (value) => {
  if (!value) return '';
  const v = String(value).toLowerCase();
  if (v.includes('annually') || v.includes('annual') || v.includes('once a year')) return 'Paid annually';
  if (v.includes('twice a year') || v.includes('half-year') || v.includes('half yearly') || v.includes('semi')) return 'Paid half-yearly';
  if (v.includes('quarterly')) return 'Paid quarterly';
  if (v.includes('twelve times a year') || v.includes('monthly')) return 'Paid monthly';
  if (v.includes('on maturity')) return 'Paid on maturity';
  if (v.includes('irregular')) return 'Paid irregularly';
  return '';
};

const truncate = (value, maxLen = 15) => {
  if (value == null) return value;
  const s = String(value);
  if (s.length <= maxLen) return s;
  return `${s.slice(0, Math.max(0, maxLen - 1))}…`;
};

const resolveCouponDisplay = (issue) => {
  const couponBasisText = String(issue.couponBasis || '').trim();
  if (couponBasisText && /zero\s*interest/i.test(couponBasisText)) {
    return { value: 'Zero Coupon', suffix: '' };
  }
  if (couponBasisText && /variable/i.test(couponBasisText)) {
    return { value: 'Variable', suffix: '' };
  }
  if (issue.couponRate != null && issue.couponRate !== '') {
    const raw = typeof issue.couponRate === 'string' && String(issue.couponRate).includes('%')
      ? String(issue.couponRate).replace('%', '')
      : issue.couponRate;
    const numRate = parseFloat(raw);
    if (Number.isFinite(numRate)) {
      return { value: `${numRate.toFixed(2)}%`, suffix: 'P.A.' };
    }
  }
  return { value: '—', suffix: '' };
};

const TodayIssueCard = ({ issue }) => {
  const issuerName = issue.companyIssuerName || '—';
  const primaryRating = pickPrimaryRating(issue.creditRatings);
  const hasCreditRating = Boolean(primaryRating?.currentRating);
  const ratingDetailLine = primaryRating
    ? `${stripPpMld(primaryRating.currentRating)} by ${getShortAgencyName(primaryRating.creditRatingAgencyName)}`
    : '—';
  const ratingDetailLineShort = truncate(ratingDetailLine, 15);

  const maturityRaw = issue.maturityDate;
  const issuePriceDisplay = formatIssuePriceInr(issue.issuePrice);
  const paymentFrequencyLabel = resolvePaymentFrequencyLabel(issue.couponPaymentFrequency);
  const { value: couponDisplayValue, suffix: couponDisplaySuffix } = resolveCouponDisplay(issue);
  const isZeroCoupon = couponDisplayValue === 'Zero Coupon';

  return (
    <article className="sb-today-issue-card" aria-label={`Primary issue: ${issuerName}`}>
      <div className="sb-today-issue-card__head">
        <div className="sb-today-issue-card__head-left">
          <h4 className="sb-today-issue-card__issuer">{issuerName}</h4>
          <p className="sb-today-issue-card__isin-line">
            ISIN:&nbsp;
            <span className="sb-today-issue-card__isin-code">{issue.isin || '—'}</span>
          </p>
        </div>
        {hasCreditRating ? (
          <div className="sb-today-issue-card__head-right">
            <div className="sb-today-issue-card__rating-pill">
              <div className="sb-today-issue-card__shield" aria-hidden>
                <HeartOutlined className="sb-today-issue-card__shield-heart" />
              </div>
              <div className="sb-today-issue-card__rating-copy">
                <span className="sb-today-issue-card__rating-label">Credit rating</span>
                <span className="sb-today-issue-card__rating-value" title={ratingDetailLine}>
                  {ratingDetailLineShort}
                </span>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <div className="sb-today-issue-card__grid">
        <div className="sb-today-issue-card__cell">
          <span className="sb-today-issue-card__cell-label">Issue price</span>
          <span className="sb-today-issue-card__cell-value">{issuePriceDisplay}</span>
        </div>
        <div className="sb-today-issue-card__cell">
          <span className="sb-today-issue-card__cell-label">Maturity date</span>
          <span className="sb-today-issue-card__cell-value">{formatDateIndian(maturityRaw)}</span>
        </div>
      </div>

      <div className="sb-today-issue-card__footer">
        <div className="sb-today-issue-card__yield">
          {!isZeroCoupon && paymentFrequencyLabel ? (
            <span className="sb-today-issue-card__yield-label" title={paymentFrequencyLabel}>
              {paymentFrequencyLabel}
            </span>
          ) : null}
          <div className="sb-today-issue-card__yield-row">
            <span className="sb-today-issue-card__yield-pct">{couponDisplayValue}</span>
            {couponDisplaySuffix ? (
              <span className="sb-today-issue-card__yield-pa">{couponDisplaySuffix}</span>
            ) : null}
          </div>
        </div>
      </div>
    </article>
  );
};

export default TodayIssueCard;
